/**
 * Search-mode state for `/:user/find`.
 *
 * SINGLE WRITER INVARIANT: only `bootstrapSearchView` and the action setters
 * write to this store. Do NOT call `useFindStore.setState(...)` from outside.
 */

import { create } from 'zustand'
import { fetchSearchGraph, fetchSearchGraphTopics } from '../hooks/useApi'
import { useGraphStore } from './useGraphStore'
import { useFilterStore } from './useFilterStore'
import { useZustand } from '../hooks/useZustand'
import { processStatements } from './bootstrap/processStatements'
import { formatTopicsFromClusters } from '../utils/graphTopics'

export const SEARCH_PHASES = {
    IDLE: 'idle',
    LOADING: 'loading',
    LOADED: 'loaded',
    EMPTY: 'empty',
    ERROR: 'error',
}

const arraysEqual = (a, b) => {
    if (a === b) return true
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
    return true
}

const dedupePreservingOrder = arr => {
    const seen = new Set()
    const out = []
    for (const t of arr) {
        const cleaned = typeof t === 'string' ? t.trim() : ''
        if (!cleaned || seen.has(cleaned)) continue
        seen.add(cleaned)
        out.push(cleaned)
    }
    return out
}

// `replaceState` (not pushState) so per-keystroke chip changes don't bloat
// browser history. Skip the write when the URL already matches to avoid
// spurious entries when terms are seeded from URL on initial load.
const writeUrl = terms => {
    if (typeof window === 'undefined' || !window.history) return
    const url = new URL(window.location.href)
    url.searchParams.delete('q')
    terms.forEach(t => url.searchParams.append('q', t))
    if (url.toString() !== window.location.href) {
        window.history.replaceState({}, '', url)
    }
}

const resetFiltersForNewSearch = () => {
    useFilterStore.getState().resetForNewSearch()
}

export const useFindStore = create((set, get) => ({
    terms: [],
    searchPhase: SEARCH_PHASES.IDLE,
    lastError: null,
    abortController: null,

    setTerms: nextRaw => {
        const next = dedupePreservingOrder(nextRaw || [])
        const prev = get().terms
        if (arraysEqual(prev, next)) return

        const controlAborter = get().abortController
        if (controlAborter) {
            try {
                controlAborter.abort()
            } catch (_) {
                /* ignore */
            }
        }

        resetFiltersForNewSearch()
        writeUrl(next)
        set({ terms: next, lastError: null, abortController: null })

        if (next.length === 0) {
            useGraphStore.getState().setGraphData(null)
            useGraphStore.getState().setLoading(false)
            set({ searchPhase: SEARCH_PHASES.IDLE })
            return
        }

        get().runFetch(next)
    },

    addTerm: term => {
        const cleaned = typeof term === 'string' ? term.trim() : ''
        if (!cleaned) return
        const current = get().terms
        if (current.includes(cleaned)) return
        get().setTerms([...current, cleaned])
    },

    removeTerm: term => {
        const next = get().terms.filter(t => t !== term)
        get().setTerms(next)
    },

    clearTerms: () => {
        get().setTerms([])
    },

    retry: () => {
        const terms = get().terms
        if (terms.length === 0) return
        const controlAborter = get().abortController
        if (controlAborter) {
            try {
                controlAborter.abort()
            } catch (_) {
                /* ignore */
            }
        }
        set({ lastError: null, abortController: null })
        get().runFetch(terms)
    },

    runFetch: async terms => {
        const controlAborter = new AbortController()
        set({
            abortController: controlAborter,
            searchPhase: SEARCH_PHASES.LOADING,
        })
        useGraphStore.getState().setLoading(true)

        try {
            const searchGraphResponse = await fetchSearchGraph(terms, {
                signal: controlAborter.signal,
            })
            // Stale guard: a newer fetch's abort() flipped this signal.
            if (controlAborter.signal.aborted) return

            useGraphStore.getState().setGraphData(searchGraphResponse)
            // Normalized accessor maps backend fields (id/content/contextId)
            // to frontend fields (uid/text/context). Statement.jsx calls
            // `.match()` on `text`, which crashes on raw backend statements.
            const normalizedStatements = useGraphStore
                .getState()
                .getStatements()
            const rawStatements =
                searchGraphResponse?.entriesAndGraphOfContext?.statements || []

            if (normalizedStatements.length > 0) {
                try {
                    await processStatements({
                        statements: normalizedStatements,
                        contextName: null,
                        addContexts: [],
                        isSynthesis: true,
                    })
                } catch (processStatementsError) {
                    console.warn(
                        '[useFindStore] processStatements failed:',
                        processStatementsError
                    )
                }
            } else {
                useFilterStore.getState().setFilteredStatements([])
            }

            set({
                searchPhase:
                    rawStatements.length > 0
                        ? SEARCH_PHASES.LOADED
                        : SEARCH_PHASES.EMPTY,
                abortController: null,
            })

            // Background fetch for AI topic names — replaces fallback
            // "Topic 1" etc. labels. Filters were just reset by setTerms,
            // so empty filter args are accurate here.
            if (rawStatements.length > 0) {
                const graphDataSnapshot = useGraphStore.getState().graphData
                fetchSearchGraphTopics(terms, {
                    filteredStatementIds: [],
                    stopWords: [],
                })
                    .then(topicsExtension => {
                        if (!topicsExtension) return
                        // Bail if a newer search took over while we waited.
                        if (
                            useGraphStore.getState().graphData !==
                            graphDataSnapshot
                        ) {
                            return
                        }
                        useGraphStore
                            .getState()
                            .applyTopicsExtension(topicsExtension)
                        const updatedClusters =
                            useGraphStore.getState().graphData
                                ?.entriesAndGraphOfContext?.graph
                                ?.graphologyGraph?.attributes
                                ?.top_clusters || []
                        if (updatedClusters.length > 0) {
                            const formattedTopics = formatTopicsFromClusters(
                                updatedClusters
                            )
                            useZustand
                                .getState()
                                .setTopics(formattedTopics, true)
                            useZustand
                                .getState()
                                .setCurrentGraphMode('topics')
                            useFilterStore
                                .getState()
                                .updateFilteredStatementsWithCommunities()
                        }
                    })
                    .catch(topicsError => {
                        console.warn(
                            '[useFindStore] AI topics failed:',
                            topicsError
                        )
                    })
            }
        } catch (fetchError) {
            if (
                fetchError?.name === 'CanceledError' ||
                fetchError?.name === 'AbortError' ||
                controlAborter.signal.aborted
            ) {
                return
            }
            console.error('[useFindStore] search failed:', fetchError)
            set({
                searchPhase: SEARCH_PHASES.ERROR,
                lastError: fetchError?.message || 'Search failed',
                abortController: null,
            })
        } finally {
            // Skip when a newer fetch's `setLoading(true)` already won.
            if (!controlAborter.signal.aborted) {
                useGraphStore.getState().setLoading(false)
            }
        }
    },
}))
